import { LoginOutlined, MenuOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Menu, Tooltip } from 'antd';
import AmountToggle from 'components/UI/AmountToggle';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import MenuItem from 'antd/lib/menu/MenuItem';
import AmountBalance from 'components/UI/AmountBalance';
import CartButton from 'components/UI/CartButton';
import DeliveryDateSelector from 'components/UI/DeliveryDateSelector';
import MobileMenu from 'components/UI/MobileMenu';
import UserMenuOverlay from 'components/UI/UserMenuOverlay';
import { BREAKPOINTS_NUM } from 'constants/Global/Breakpoints.const';
import useWindowSize from 'hooks/window-size';
import BuyIntention from 'screens/BuyIntention';
import MyOrderIntentionEdit from 'screens/MyOrderIntentions/myIntentionEdit';
import {
  getTermProductList,
  setCustomerIdSelected,
  setPageProductList,
  setTermProductList,
} from 'services/config';
import {
  useGetBalanceQuery,
  useGetSummaryCartQuery,
} from 'services/ecommerceApi';
import {
  buyIntenteionSelectors,
  cartSelectors,
  myOrderIntentionEditSelectors,
  offerFiltersSelectors,
  sessionSelectors,
} from 'store/state/selectors';
import {
  buyIntentionActions,
  cartActions,
  errorActions,
  offerFiltersActions,
  sessionActions
} from 'store/state/slices';
import { dateUtils } from 'utils/date';
import { notSpecialCharacters } from 'utils/util';

import * as S from './styles';

function Header() {
  const UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST';
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [width] = useWindowSize();
  const { t } = useTranslation('translation', { keyPrefix: 'header' });
  const ssoUrl = process.env.REACT_APP_SSO_LOGIN_URL ?? '';
  const [showBalance, setShowBalance] = useState(false);
  const [error] = useState<{ status: string; message: string }>();

  const {
    name: userName,
    customers,
    producerUrl,
  } = useSelector(sessionSelectors.getRoot);
  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);
  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );
  const producerUrls = useSelector(sessionSelectors.getUrlsProducer);
  const totalVol = useSelector(cartSelectors.getVolTotal);
  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );
  const filter = useSelector(offerFiltersSelectors.getOfferFilters);
  const showBuyIntentionModal = useSelector(
    buyIntenteionSelectors.getShowIntentionModal,
  );
  const showMyOrderIntentionEditModal = useSelector(
    myOrderIntentionEditSelectors.getShowIntentionModal,
  );
  const [customerSelected, setCustomerSelected] = useState(
    customers.find((customer) => customer.id === selectedCustomerId),
  );

  const [urlSelected, setUrlSelected] = useState<string>('');
  const [searchValue, setSearchValue] = useState(
    getTermProductList() !== '' ? getTermProductList() : filter.name,
  );
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  useEffect(() => {
    setSearchValue((value) => {
      const newValue = filter.name !== value ? filter.name : value;
      setTermProductList(newValue || '');
      return newValue;
    });
  }, [filter.name]);

  useEffect(() => {
    setUrlSelected(producerUrl);
  }, [producerUrl]);

  const toggleBalance = useCallback(() => {
    setShowBalance((value) => !value);
  }, []);

  const { data: summaryCart, isError: errorRequestSummary } =
    useGetSummaryCartQuery(
      {
        customerId: selectedCustomerId,
      },
      { skip: !isAuthenticated || !selectedCustomerId },
    );

  const {
    data: balance,
    isLoading: isLoadingBalance,
    isFetching: isFetchingBalance,
    isError: err,
  } = useGetBalanceQuery(customerSelected?.accountCode || '', {
    skip: !customerSelected?.accountCode,
  });

  useEffect(() => {
    if (selectedCustomerId) {
      setCustomerSelected(
        customers.find((customer) => customer.id === selectedCustomerId),
      );
      const customerData = customers.find(
        (customer) => customer.id === selectedCustomerId,
      );

      dispatch(buyIntentionActions.setCustomer(customerData));
      dispatch(sessionActions.setSelectedCustomerFilter(selectedCustomerId));
    }
    const customerData = customers.find(
      (customer) => customer.id === selectedCustomerId,
    );
    dispatch(sessionActions.setCustomer(customerData));
  }, [selectedCustomerId, customers, dispatch]);

  useEffect(() => {
    if (summaryCart && summaryCart.totalItems > 0 && !errorRequestSummary) {
      dispatch(cartActions.setSummaryCart(summaryCart));
    }
    if (summaryCart && totalVol > 0) {
      dispatch(
        sessionActions.setBillingDate(
          dateUtils.createDateWithTimeZone(summaryCart.billingDate),
        ),
      );
      dispatch(
        sessionActions.setDeliveryDate(
          dateUtils.createDateWithTimeZone(summaryCart.deliveryDate),
        ),
      );
    }
  }, [dispatch, summaryCart, totalVol, errorRequestSummary]);

  useEffect(() => {
    if (errorRequestSummary) {
      dispatch(
        cartActions.setSummaryCart({
          lkpOfferId: 0,
          volOfferId: 0,
          customerId: Number.NaN,
          volTotal: 0,
          lkpTotal: 0,
          totalItems: 0,
          user: '',
          deliveryDate: '',
          billingDate: '',
          volExpiration: '',
          lkpExpiration: '',
          totalLkpItems: 0,
          totalVolItems: 0,
          lKpCreationDate: '',
          volCreationDate: '',
        }),
      );
    }
  }, [dispatch, errorRequestSummary]);

  const filterProductsByName = useCallback(
    (term) => {
      dispatch(offerFiltersActions.filter({ ...filter, name: term }));
      setPageProductList(1);
      setTermProductList(term);
      navigate(`/${producerUrl}/products/`);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [dispatch, navigate, filter, producerUrl],
  );

  const handleChangeUrlSelected = (value: string) => {
    setUrlSelected(value);
    navigate(`${location.pathname.replace(producerUrl, value)}`);
  };

  const searchPlaceholder =
    width > BREAKPOINTS_NUM.tabletMini ? t('search') : t('searchVOL');

  const onSearchBarInputChange = ({ target: { value } }) => {
    if (value.length === 0) {
      setTermProductList(value);
      dispatch(
        offerFiltersActions.filter({
          ...filter,
          name: value,
        }),
      );
    }
    setSearchValue(value);
  };

  const onSearchBarInputKeyPress = (event) => {
    if (event.key === 'Enter') {
      filterProductsByName(searchValue);
    } else {
      notSpecialCharacters(event);
    }
  };

  const handleSignInButtonClick = () => {
    window.location.href = ssoUrl;
    return null;
  };

  const handleLogoClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const setSelectedCustomer = useCallback(
    (value) => {
      dispatch(sessionActions.setSelectedCustomer(value));
      dispatch(sessionActions.setSelectedCustomerFilter(value));

      const customerData = customers.find((customer) => customer.id === value);
      dispatch(buyIntentionActions.setCustomer(customerData));
      setCustomerIdSelected(value);
    },
    [dispatch, customers],
  );

  const formatCustomerName = (customerName: string) => {
    if (customerName.length <= 22) return customerName;

    return `${customerName.substring(0, 22)}...`;
  };

  const selectedCustomer = customers.find(
    ({ id }) => id === selectedCustomerId,
  );

  const filteredCustomers = customers.filter(({ accountCode, name }) => {
    const searchLower = customerSearchTerm.toLowerCase();
    return (
      accountCode.toLowerCase().includes(searchLower) ||
      name.toLowerCase().includes(searchLower)
    );
  });

  return (
    <S.Root>
      <S.Logo onClick={handleLogoClick} />
      {isAuthenticated && (
        <S.UserMenu>
          <S.Text small>{t('welcome')}</S.Text>
          <Dropdown
            arrow
            overlay={
              <Menu>
                <UserMenuOverlay />
              </Menu>
            }
            placement="bottomRight"
          >
            <S.UserMenuButton>
              <S.UserName>{userName}</S.UserName>
              <S.DownOutlined />
            </S.UserMenuButton>
          </Dropdown>
        </S.UserMenu>
      )}
      <S.SearchBar>
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={onSearchBarInputChange}
          onKeyPress={onSearchBarInputKeyPress}
        />
        <Tooltip title="Buscar">
          <Button
            onClick={() => filterProductsByName(searchValue)}
            icon={<SearchOutlined />}
          />
        </Tooltip>
      </S.SearchBar>
      {!isAuthenticated && (
        <S.SignIn onClick={handleSignInButtonClick}>
          <S.Text>{t('signin')}</S.Text>
          <LoginOutlined />
        </S.SignIn>
      )}
      <S.RightAlignedContainer>
        {isAuthenticated && isProducerAuthenticated && !!(producerUrls?.length >= 1) && (
          <S.ClientMenu>
            <S.Text small>{t('producer')}</S.Text>
            {producerUrls.length > 1 ? (
              <Dropdown
                arrow
                overlay={
                  <Menu>
                    {producerUrls.map((url) => (
                      <MenuItem
                        key={url.url}
                        onClick={() => handleChangeUrlSelected(url.url)}
                      >
                        {url.url}
                      </MenuItem>
                    ))}
                  </Menu>
                }
                placement="bottomRight"
              >
                <S.ClientMenuButton>
                  <S.UserName>{urlSelected}</S.UserName>
                  <S.DownOutlined />
                </S.ClientMenuButton>
              </Dropdown>
            ) : (
              <S.ClientMenuButton>
                <S.UserName>{urlSelected}</S.UserName>
              </S.ClientMenuButton>
            )}
          </S.ClientMenu>
        )}
        {isAuthenticated &&
          !isProducerAuthenticated &&
          !!(customers?.length >= 1) && (
            <S.ClientMenu>
              <S.Text small>{t('client')}</S.Text>
              {customers.length > 1 ? (
                <Dropdown
                  arrow
                  overlay={(
                    <Menu>
                      <S.SearchInputWrapper>
                        <Input
                          placeholder="Buscar cliente..."
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          prefix={<SearchOutlined />}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </S.SearchInputWrapper>
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map(({ id, accountCode, name }) => (
                          <MenuItem
                            key={`${id}-key-customer`}
                            onClick={() => {
                              setSelectedCustomer(id);
                              setCustomerSearchTerm('');
                            }}
                          >
                            {`${accountCode} - ${formatCustomerName(name)}`}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          Nenhum cliente encontrado
                        </MenuItem>
                      )}
                    </Menu>
                  )}
                  placement="bottomRight"
                >
                  <S.ClientMenuButton>
                    <S.UserName>
                      {selectedCustomer?.accountCode} -{' '}
                      {formatCustomerName(selectedCustomer?.name || '')}
                    </S.UserName>
                    <S.DownOutlined />
                  </S.ClientMenuButton>
                </Dropdown>
              ) : (
                <S.ClientMenuButton>
                  <S.UserName>
                    {selectedCustomer?.accountCode} -{' '}
                    {formatCustomerName(selectedCustomer?.name || '')}
                  </S.UserName>
                </S.ClientMenuButton>
              )}
            </S.ClientMenu>
          )}
        {isAuthenticated && !isProducerAuthenticated && (
          <S.UserAmount>
            <S.Text small>{t('availableValue')}</S.Text>
            <S.AmountDisplay>
              <AmountBalance
                showBalance={showBalance}
                isLoadingBalance={isLoadingBalance}
                isFetchingBalance={isFetchingBalance}
                balance={balance}
              />
              <AmountToggle
                showValue={!showBalance}
                warning={err}
                setShowValue={toggleBalance}
              />
            </S.AmountDisplay>
          </S.UserAmount>
        )}
        {isAuthenticated && (
          <S.HideOnMobile>
            <DeliveryDateSelector />
          </S.HideOnMobile>
        )}
      </S.RightAlignedContainer>
      {isAuthenticated && !isProducerAuthenticated && <CartButton />}
      {isAuthenticated && (
        <S.MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
          <MenuOutlined />
        </S.MobileMenuButton>
      )}
      {isAuthenticated && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          setOpen={setMobileMenuOpen}
        />
      )}
      {showBuyIntentionModal ? <BuyIntention /> : null}
      {showMyOrderIntentionEditModal ? <MyOrderIntentionEdit /> : null}
    </S.Root>
  );
}
export default React.memo(Header);
